import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';

/**
 * Quote request link of the product page: "Solicitar este modelo" for a product marked "Bajo pedido"
 * (`snippets/pc-on-demand-request.liquid`) and "Solicitar cotización por volumen" under the add to cart button
 * (`snippets/pc-quote-button.liquid`).
 *
 * The link is `<quote page>?items=SKU:size:pairs`, the format the quote builder reads. This component keeps it right:
 * - when the visitor picks another size, the variant picker fetches the page for that variant and the new link is taken
 *   from that response, so the quote opens with the size that is on screen;
 * - with `data-follow-quantity` (the product can be bought, so it has a quantity input), the pairs of the link follow the
 *   quantity, like the prototype: the quote opens with the model and the quantity the visitor typed.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} [link] - The link (not rendered when the store has no quote page).
 *
 * @extends {Component<Refs>}
 */
class PcQuoteRequest extends Component {
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    const section = this.closest('.shopify-section, dialog');
    section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });

    if (this.dataset.followQuantity !== undefined) {
      const scope = this.closest('product-component') ?? section ?? document;
      scope.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate, { signal });
      scope.addEventListener('input', this.#onInput, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abort?.abort();
    this.#abort = undefined;
  }

  /**
   * Takes the link of the newly selected variant from the section response.
   * @param {Event} event
   */
  #onProductSelect = (event) => {
    const { promise } = /** @type {{ promise?: Promise<{ detail?: { html?: Document } }> }} */ (event);
    if (!promise) return;

    promise
      .then(({ detail }) => {
        const source = detail?.html?.querySelector(`pc-quote-request[data-block-id="${this.dataset.blockId}"] [ref="link"]`);
        const href = source?.getAttribute('href');
        if (href && this.refs.link) {
          this.refs.link.href = href;
          this.#writePairs(this.#readQuantity());
        }
      })
      .catch(() => {
        // Aborted or failed selection: keep the current link
      });
  };

  /** @param {Event} event */
  #onQuantityUpdate = (event) => {
    const quantity = /** @type {CustomEvent<{ quantity?: number }>} */ (event).detail?.quantity;
    this.#writePairs(Number.isFinite(quantity) && /** @type {number} */ (quantity) > 0 ? /** @type {number} */ (quantity) : this.#readQuantity());
  };

  /** @param {Event} event */
  #onInput = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.name === 'quantity') this.#writePairs(this.#readQuantity());
  };

  /** @returns {number} The quantity in the input of this product, 0 while it is empty or not a number. */
  #readQuantity() {
    const scope = this.closest('product-component') ?? this.closest('.shopify-section') ?? document;
    const input = scope.querySelector('input[name="quantity"]');
    const value = input instanceof HTMLInputElement ? parseInt(input.value, 10) : NaN;
    return Number.isFinite(value) && value > 0 ? value : 0;
  }

  /**
   * Writes the pairs into the `items` of the link ("SKU:size:pairs").
   * @param {number} pairs
   */
  #writePairs(pairs) {
    const { link } = this.refs;
    if (!link || !pairs) return;

    const url = new URL(link.getAttribute('href') ?? '', window.location.origin);
    const [sku = '', size = ''] = (url.searchParams.get('items') ?? '').split(':');
    if (!sku) return;

    url.searchParams.set('items', `${sku}:${size}:${pairs}`);
    link.href = `${url.pathname}${url.search}`;
  }
}

if (!customElements.get('pc-quote-request')) {
  customElements.define('pc-quote-request', PcQuoteRequest);
}
